import { ca } from "date-fns/locale";
import { api } from "./api";
import { RankingDataDailyUpdate } from "../types/send-ranking-data";

interface PlayerData {
  _id: string;
  name: string;
  points: string;
  updatedAt: string;
}

interface RankingPayload {
  _id: string;
  players: PlayerData[];
}


function convertToCSVUrl(url: string): string {
  const sheetIdMatch = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
  if (!sheetIdMatch) {
    throw new Error("URL do Google Sheets inválida. Verifique se a URL está correta.");
  }
  
  const sheetId = sheetIdMatch[1];
  
  // Converter para URL de exportação CSV
  return `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;
}


function normalizeNameToId(name: string): string {
  const firstName = name.split(' ')[0]; // Pega só o primeiro nome
  
  return firstName
    .toLowerCase()
    .normalize('NFD') // Decompor caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remover diacríticos (acentos)
    .replace(/[^a-z0-9]/g, '') // Remover caracteres especiais
    .trim();
}


export async function readGoogleSheets(googleSheetsUrl: string): Promise<PlayerData[]> {
  try {
    const csvUrl = convertToCSVUrl(googleSheetsUrl);
    
    const response = await fetch(csvUrl);
    
    if (!response.ok) {
      throw new Error(`Erro ao acessar planilha: ${response.status} - ${response.statusText}`);
    }
    
    const csvText = await response.text();
    
    console.log('CSV Raw Text:', csvText);
    console.log('CSV Raw Text Length:', csvText.length);
    
    let lines = csvText.split('\n').filter(line => line.trim() !== '');
    
    if (lines.length === 1) {
      lines = csvText.split('\r\n').filter(line => line.trim() !== '');
    }
    
    if (lines.length === 1) {
      lines = csvText.split('\r').filter(line => line.trim() !== '');
    }
    
    console.log('Lines parsed:', lines);
    console.log('Total lines found:', lines.length);
    
    if (lines.length < 1) {
      throw new Error("A planilha deve conter pelo menos uma linha de dados.");
    }
    
    // Limpar caracteres de quebra de linha de todas as linhas
    lines = lines.map(line => line.replace(/\r/g, '').trim());
    
    console.log('Lines after cleaning:', lines);
    
    // Detectar se a primeira linha é cabeçalho
    const firstLine = lines[0].split(',').map(col => col.trim().replace(/"/g, ''));
    let startIndex = 0;
    
    // Se a segunda coluna da primeira linha não é um número, provavelmente é cabeçalho
    if (firstLine.length >= 2) {
      const secondColumnAsNumber = parseFloat(firstLine[1]);
      if (isNaN(secondColumnAsNumber)) {
        console.log('Primeira linha detectada como cabeçalho:', firstLine);
        startIndex = 1; 
      } else {
        console.log('Primeira linha detectada como dados:', firstLine);
        startIndex = 0; 
      }
    }
    
    const players: PlayerData[] = [];
    const currentTime = new Date().toISOString();
    
    if (startIndex >= lines.length) {
      throw new Error("Nenhuma linha de dados encontrada após o cabeçalho.");
    }
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i];
      console.log(`Processing line ${i + 1}:`, line);
      
      const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
      console.log(`Columns for line ${i + 1}:`, columns);
      
      if (columns.length < 2) {
        console.warn(`Linha ${i + 1} ignorada: deve conter pelo menos 2 colunas (nome e pontos)`);
        continue;
      }
      
      const name = columns[0]; // Primeira coluna: nome
      const pointsStr = columns[1]; // Segunda coluna: pontos
      
      // Validar se o nome não está vazio
      if (!name || name.trim() === '') {
        console.warn(`Linha ${i + 1} ignorada: nome não pode estar vazio`);
        continue;
      }
      
      // Validar se os pontos são numéricos
      const points = parseFloat(pointsStr);
      if (isNaN(points)) {
        console.warn(`Linha ${i + 1} ignorada: pontos devem ser um número válido (encontrado: "${pointsStr}")`);
        continue;
      }
      
      // Usar o valor exato da primeira coluna como _id, apenas garantindo que não haja espaços
      const rawId = columns[0] ? String(columns[0]).trim() : '';
      const playerId = rawId.replace(/\s+/g, ''); // remove todos os espaços

      if (!playerId) {
        console.warn(`Linha ${i + 1} ignorada: _id inválido gerado a partir de "${columns[0]}"`);
        continue;
      }

      players.push({
        _id: playerId,
        name: name.trim(),
        points: points.toString(),
        updatedAt: currentTime
      });
    }
    
    console.log('Total players processed:', players.length);
    console.log('Players array:', players);
    
    if (players.length === 0) {
      throw new Error("Nenhum dado válido foi encontrado na planilha. Verifique o formato dos dados.");
    }
    
    return players;
    
  } catch (error) {
    console.error('Erro ao ler Google Sheets:', error);
    
    if (error instanceof Error) {
      throw error;
    }
    
    throw new Error('Erro desconhecido ao processar planilha');
  }
}


export async function sendRankingData(players: PlayerData[]): Promise<any> {
  try {
    const payload: RankingPayload[] = [{
      _id: "ranking_default",
      players: players
    }];
    
    console.log('Enviando payload:', payload);

    const response = await api.post('database/ranking_data/bulk', payload);
    
    console.log('Resposta do servidor:', response.data);
    
    return response.data;
    
  } catch (error) {
    console.error('Erro ao enviar dados do ranking:', error);
    throw error;
  }
}


export async function importRankingFromGoogleSheets(googleSheetsUrl: string): Promise<{
  success: boolean;
  total: number;
  players: PlayerData[];
  message: string;
}> {
  try {
    // Validar URL
    if (!googleSheetsUrl || googleSheetsUrl.trim() === '') {
      throw new Error('URL do Google Sheets é obrigatória');
    }
    
    // Verificar se é uma URL válida do Google Sheets
    if (!googleSheetsUrl.includes('docs.google.com/spreadsheets')) {
      throw new Error('URL deve ser de uma planilha do Google Sheets (docs.google.com/spreadsheets)');
    }
    
    // Ler dados da planilha
    console.log('Lendo dados do Google Sheets...');
    const players = await readGoogleSheets(googleSheetsUrl);
    
    console.log(`${players.length} jogadores processados com sucesso`);
    
    // Enviar para API
    console.log('Enviando dados para servidor...');
    await sendRankingData(players);
    
    return {
      success: true,
      total: players.length,
      players: players,
      message: `Importação concluída! ${players.length} jogadores foram processados com sucesso.`
    };
    
  } catch (error) {
    console.error('Erro na importação:', error);
    
    return {
      success: false,
      total: 0,
      players: [],
      message: error instanceof Error ? error.message : 'Erro desconhecido na importação'
    };
  }
}


// funcao que envia dados de planilja Incremental local
export async function sendRankingDataIncremental(data: RankingDataDailyUpdate){
  try{
    const payload = data;

    const response = await api.post('/database/data_import__c', payload);
    return response.data;

  }catch(error){
    console.error('Erro ao enviar dados incrementais do ranking:', error);
    throw error;
  }
}